import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Alert, Spinner } from "../../../components/ui";
import type { Role } from "../../../app/session/SessionProvider";
import { AppShell } from "../../../layouts/AppShell";
import { getQuestionnaire } from "../../questionnaire/api/questionnaireAdminApi";
import { getCurrentQuestionnaireSubmission } from "../../questionnaire/api/questionnaireResponseApi";

function FeatureCard({
  title,
  description,
  to,
  variant,
}: {
  title: string;
  description: string;
  to: string;
  variant: "primary" | "success";
}) {
  return (
    <div className="col-xl-6 col-md-6 mb-4">
      <Link
        className={`card border-left-${variant} shadow h-100 py-3 text-decoration-none`}
        to={to}
      >
        <div className="card-body">
          <div className="row no-gutters align-items-center">
            <div className="col mr-2">
              <div
                className={`text-xs font-weight-bold text-${variant} text-uppercase mb-2`}
              >
                {title}
              </div>
              <div className="h5 mb-2 font-weight-bold text-gray-800">
                {title}
              </div>
              <p className="mb-0 text-gray-600">{description}</p>
            </div>
            <div className="col-auto">
              <span
                className={`rounded-circle bg-${variant} text-white d-inline-flex align-items-center justify-content-center`}
                style={{ width: 52, height: 52, fontSize: 24 }}
                aria-hidden="true"
              >
                {title === "教材" ? "書" : "卷"}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

function StudentQuestionnaireStatus() {
  const questionnaireQuery = useQuery({
    queryKey: ["questionnaire"],
    queryFn: getQuestionnaire,
  });
  const submissionQuery = useQuery({
    queryKey: ["questionnaire", "current-submission", questionnaireQuery.data?.versionId],
    queryFn: getCurrentQuestionnaireSubmission,
    enabled: questionnaireQuery.data?.availability.status === "OPEN",
    retry: false,
  });

  if (questionnaireQuery.isLoading || submissionQuery.isLoading) {
    return <Spinner label="載入問卷狀態" />;
  }

  if (questionnaireQuery.isError || submissionQuery.isError || !questionnaireQuery.data) {
    return <Alert variant="danger">暫時無法確認問卷狀態，請稍後再試。</Alert>;
  }

  if (questionnaireQuery.data.availability.status === "CLOSED") {
    return <Alert variant="warning">問卷目前未開放，開放後可從這裡直接前往填寫。</Alert>;
  }

  if (submissionQuery.data) {
    return (
      <Alert variant="success">
        已完成問卷：您已提交目前版本 V{questionnaireQuery.data.versionNumber}。
        <Link className="alert-link ml-2" to="/questionnaire">
          前往修改問卷
        </Link>
      </Alert>
    );
  }

  return (
    <Alert variant="info">
      尚未完成問卷：目前開放版本為 V{questionnaireQuery.data.versionNumber}。
      <Link className="alert-link ml-2" to="/questionnaire">
        立即填寫問卷
      </Link>
    </Alert>
  );
}

export function DashboardPage({ role }: { role: Role }) {
  const questionnaireTo =
    role === "EDUCATOR_ADMIN" ? "/admin/questionnaire" : "/questionnaire";
  const questionnaireDescription =
    role === "EDUCATOR_ADMIN"
      ? "管理問卷收件狀態、查看填答名冊與結果"
      : "填寫數產署效能評估工具使用問卷";
  return (
    <AppShell title="總覽" breadcrumb={["總覽"]}>
      {role === "STUDENT" && (
        <div className="mb-4">
          <StudentQuestionnaireStatus />
        </div>
      )}
      <div className="row">
        <FeatureCard
          title="教材"
          description="瀏覽及下載平台提供的學習教材"
          to="/materials"
          variant="primary"
        />
        <FeatureCard
          title="問卷"
          description={questionnaireDescription}
          to={questionnaireTo}
          variant="success"
        />
      </div>
    </AppShell>
  );
}
